// src/auth/persistKeys.js
//
// Single source of truth for the localStorage keys of persist-backed Zustand
// stores, and their cross-user scoping.
//
// WHY this module exists: localStorage is per-origin, not per-user. On a
// shared browser profile, a persisted store hands the next user the previous
// user's state unless we wipe it at a different-user login (see sessionGuard).
// The wipe list used to be a hand-maintained array inside sessionGuard, and it
// silently drifted out of sync with the actual set of persist stores —
// "aiprime-layer-state" and "aiprime-imagery-ui" were both missing, so a new
// user inherited the previous user's layer toggles / view prefs. Centralizing
// the keys here — paired with persistKeys.test.js, which fails CI if any
// persist store's name isn't classified below — turns that silent omission
// into a loud one.
//
// CONTRACT:
//   1. Every persist-backed store imports its key from PERSIST_KEYS for its
//      persist() `name` (so the literal lives in exactly one place).
//   2. Every key appears in exactly one scope list below.
//   3. persistKeys.test.js asserts the two stay in sync with the real stores.

export const PERSIST_KEYS = {
  activeOrg:   "aiprime.activeOrg.v1",
  project:     "aiprime.project.v3",
  lastSession: "aiprime.last_session.v1",
  layerState:  "aiprime-layer-state",
  caseBuilderDraft: "padrekko-case-builder-draft",
  basemap:     "aiprime-basemap",
  routing:     "aiprime.routing.v1",
  wellfinderAoi: "aiprime.wellfinder.aoi.v1",
  recheckAoi:  "aiprime.recheck.aoi.v1",
};

// USER-SCOPED — wiped when a DIFFERENT user logs in on the same browser
// profile. Anything tied to a user's identity, entitlements, or workspace.
// Same-user logout/login preserves these (see sessionGuard's stamp check).
export const USER_SCOPED_PERSIST_KEYS = [
  PERSIST_KEYS.activeOrg,
  PERSIST_KEYS.project,
  PERSIST_KEYS.lastSession,
  PERSIST_KEYS.layerState, // layer toggles/opacity/bounds — a new user should
                           // not inherit the previous user's view prefs.
  // NB: "aiprime-imagery-ui" was classified here until useImageryStore.js was
  // deleted as unreachable. Copies still sit in browsers that ran an older
  // build. They are now cleared by the "aiprime-" prefix sweep below rather
  // than by being named here — which is the point of inverting the rule: a key
  // does not have to be remembered to be wiped.
  PERSIST_KEYS.caseBuilderDraft, // in-progress "Create New Case" draft — a
  // different user must not inherit a half-built case.
  PERSIST_KEYS.routing, // last successful road route — a route to a pad is a
  // statement about someone's entitled acreage, so the next user on a shared
  // field tablet must not inherit it.
  PERSIST_KEYS.wellfinderAoi, // the reviewer's last operating AOI. Same
  // reasoning as routing: an AOI names a customer's acreage, and it is also an
  // ASSIGNMENT — inheriting the previous reviewer's workbench would silently
  // put someone on the wrong queue while looking exactly like their own last
  // session.
  PERSIST_KEYS.recheckAoi, // the re-checker's last operating AOI, for the
  // same reasons as wellfinderAoi. Its own key: the two queues are worked
  // separately, and picking an area in one must not re-scope the other.
];

// DEVICE-SCOPED — survive a user change (browser/device-level prefs not tied
// to identity, e.g. a theme). Classify here to preserve a key across users
// instead of wiping it.
export const DEVICE_SCOPED_PERSIST_KEYS = [
  PERSIST_KEYS.basemap, // which classic Mapbox style is shown — a display
  // preference, not tied to identity or entitlements.
];

// Every classified key. The guard test asserts this set equals the set of
// localStorage names actually declared by the app's persist stores.
export const ALL_PERSIST_KEYS = [
  ...USER_SCOPED_PERSIST_KEYS,
  ...DEVICE_SCOPED_PERSIST_KEYS,
];

// ── The cross-user wipe: deny by default ────────────────────────────────────
//
// sessionGuard used to wipe USER_SCOPED_PERSIST_KEYS by exact key. That is an
// allowlist of things to delete, and it fails in two directions at once:
//
//   - A key nobody remembered to classify was never wiped. That is the bug this
//     module was created to fix, and it recurred: "aiprime-layer-state" and
//     "aiprime-imagery-ui" were both missing from the original hand list.
//   - A key REMOVED from the registry stops being wiped, so copies already in
//     users' browsers survive forever with nothing left to clear them. Deleting
//     useImageryStore.js did exactly that to "aiprime-imagery-ui".
//
// Both are silent. Nothing observes the key that was missed. So the rule is
// inverted below: sweep everything under the app's prefixes and preserve an
// explicit few. The failure mode becomes "a key we forgot to allowlist gets
// wiped" — a user loses a display preference and says so — instead of "state
// leaks between users on a shared browser profile and nobody finds out."
//
// Do not narrow this back to an exact-key list. It is deliberately over-broad.
export const SWEPT_KEY_PREFIXES = [
  "aiprime.",   // aiprime.activeOrg.v1, aiprime.project.v3, aiprime.last_session.v1
  "aiprime-",   // aiprime-layer-state, aiprime-basemap, and retired keys like
                // aiprime-imagery-ui still sitting in older browsers
  "padrekko-",  // padrekko-case-builder-draft
  "debug.",     // debug.as_org — the platform-admin impersonation override
];

// Keys swept despite not matching a prefix. Kept separate so the list stays
// short and each entry has to justify itself.
export const SWEPT_EXTRA_KEYS = [
  // Read as a bearer by stores/useDatasetsStore.js. Nothing in the tree writes
  // it, so it is vestigial today — but a stale bearer must not outlive a user
  // change if something starts writing it again.
  "kc_token",
];

// Keys that MUST survive a different-user login. This is the allowlist, and it
// is the only thing standing between a key and deletion.
export const PRESERVED_ACROSS_USERS = [
  ...DEVICE_SCOPED_PERSIST_KEYS,
  // The user stamp itself. sessionGuard rewrites it immediately after the wipe,
  // so removing it would work by accident; listing it means a future reorder of
  // that function cannot turn every login into a first login.
  "aiprime.session.userSub",
];
